// User preferences — synced to the server via PATCH /api/auth/me.
//
// Pattern:
//   1. On boot, main.tsx stores the user from GET /api/auth/me on
//      window.__cortexUser. Prototype files read preferences from there.
//   2. On every local change (column resize, language switch, onboarding
//      dismiss), call `savePreference(key, value)` which debounces a
//      PATCH /api/auth/me with the updated uiPreferences.
//   3. Falls back to localStorage if the API call fails (offline resilience).
//
// This module is designed to be called from prototype .jsx files via
// `window.__savePreference(key, value)` — exposed in main.tsx.

import type { Lang } from '@cortex/i18n';
import type { UiPreferences } from './auth';

let _debounceTimer: ReturnType<typeof setTimeout> | null = null;
let _pendingPatch: Partial<UiPreferences> = {};
const DEBOUNCE_MS = 500;

function getUser(): { uiPreferences?: UiPreferences | null } | null {
  return (typeof window !== 'undefined' && (window as any).__cortexUser) || null;
}

function flushToServer(): void {
  const patch = { ..._pendingPatch };
  _pendingPatch = {};
  if (Object.keys(patch).length === 0) return;

  fetch('/api/auth/me', {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ uiPreferences: patch }),
  }).catch(() => {
    // Server unreachable — localStorage fallback already written by callers.
  });
}

export function savePreference<K extends keyof UiPreferences>(
  key: K,
  value: UiPreferences[K],
): void {
  // Update the in-memory user immediately so reads within the same
  // render cycle see the new value.
  const user = getUser();
  if (user) {
    if (!user.uiPreferences) user.uiPreferences = {};
    (user.uiPreferences as any)[key] = value;
  }

  // Accumulate into the pending patch and debounce the server write.
  (_pendingPatch as any)[key] = value;
  if (_debounceTimer) clearTimeout(_debounceTimer);
  _debounceTimer = setTimeout(flushToServer, DEBOUNCE_MS);
}

export function saveLanguage(lang: Lang): void {
  fetch('/api/auth/me', {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ language: lang }),
  }).catch(() => {});
}

export function readPreference<K extends keyof UiPreferences>(
  key: K,
): UiPreferences[K] | undefined {
  const user = getUser();
  return user?.uiPreferences?.[key];
}
